/**
 * The panel — level two of D1: everything that changed, whenever somebody wants it.
 *
 * Three sections in one scroller: the notices valid right now, what is new for this reader,
 * then the complete history. The history is never filtered and never paged: it lives in the
 * bundle, it is a few dozen lines, and "I know it changed, I just cannot find when" is the
 * question this half answers.
 *
 * ## Current notices — D5 of the Reporting App's central-notices plan (M3, 2026-09-24)
 *
 * A notice closed in the bar is still readable here, for exactly as long as it is valid. The
 * section shows what the wire carries and nothing more: no dismissed flag, no archive, and a
 * notice whose window closes while the panel is open leaves the section on its own timer —
 * **an expired notice appears nowhere** (D5). The count is in the heading, and the button's dot
 * does NOT light for a notice (D8): the dot belongs to the entries, and a dot that kept shining
 * after the bar was closed would be the wallpaper the entries' own D7 forbids.
 *
 * ## Opening the panel is what clears the dot — and not the markers
 *
 * `openPanel()` marks the new entries seen (the provider does it, in the same click). The
 * markers on the elements survive that, deliberately: they go when the element is USED. So the
 * "New" section is frozen at the moment of opening — otherwise the list would empty itself
 * under the reader's eyes the instant the POST returned.
 *
 * ## "Show me"
 *
 * Closes the panel, asks the app to reveal the place, and highlights it for a moment. It is
 * offered only for an entry whose anchor is mounted right now AND was registered with a way to
 * reveal it: an offer that would do nothing is worse than no offer, and only the app knows
 * whether the element exists on this screen and how to get there.
 *
 * ## The images
 *
 * Fetched with the bearer token and shown from a blob URL (D4) — an `<img src>` cannot carry an
 * `Authorization` header, and a screenshot of this app carries a customer name, a turnover and
 * a margin, so it may not sit in the anonymous bundle. A failure renders as a thin line of
 * text, never as a broken-image icon: the entry is still worth reading without its picture.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Info, X } from 'lucide-react';
import { useWhatsNew } from './WhatsNewProvider';
import { NOTICE_STYLES } from './NoticeBar';
import type { Notice, WhatsNewEntry, WhatsNewKind } from './types';

/** One word per kind, in the reader's language — not the repository's (Regel 14, D7). */
const KIND_LABEL: Record<WhatsNewKind, string> = {
    new: 'New',
    improved: 'Improved',
    fixed: 'Fixed',
};

/**
 * Three tints from the app's own palette, and none of them is amber.
 *
 * Amber means "somebody has to act" here (design.md §2). A fixed bug is news, not a task, so
 * `fixed` takes a neutral and `new` takes the brand green. `improved` sits on the informational
 * blue — the one state that is neither won nor lost.
 */
const KIND_CLASS: Record<WhatsNewKind, string> = {
    new: 'bg-blue-50 text-blue-700 border-blue-200',
    improved: 'bg-info-50 text-info-700 border-info-200',
    fixed: 'bg-gray-100 text-gray-600 border-gray-200',
};

function EntryImage({ imageId }: { imageId: string }) {
    const { loadImage } = useWhatsNew();
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void loadImage(imageId).then(result => {
            if (cancelled) return;
            if (result) setUrl(result);
            else setFailed(true);
        });
        return () => {
            cancelled = true;
        };
        // The provider owns the blob URLs and revokes them when the panel closes; revoking here
        // would pull the picture out from under a second entry using the same image.
    }, [imageId, loadImage]);

    if (failed) return <p className="mt-2 text-xs text-gray-400">The picture for this entry could not be loaded.</p>;
    if (!url) return <div className="mt-2 h-24 w-full animate-pulse rounded-md bg-gray-100" />;

    return (
        <img
            src={url}
            alt=""
            className="mt-2 w-full rounded-md border border-gray-200"
            loading="lazy"
        />
    );
}

function EntryCard({ entry, highlight }: { entry: WhatsNewEntry; highlight: boolean }) {
    const { showMe, canRevealAnchor } = useWhatsNew();
    const canShow = !!entry.anchor && canRevealAnchor(entry.anchor);

    return (
        <li
            className={`rounded-lg border p-3 ${
                highlight ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
            }`}
        >
            <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${KIND_CLASS[entry.kind]}`}>
                    {KIND_LABEL[entry.kind]}
                </span>
                <time className="text-[11px] text-gray-400" dateTime={entry.date}>
                    {entry.date}
                </time>
            </div>
            <h3 className="mt-1.5 text-sm font-bold text-gray-900">{entry.title}</h3>
            <p className="mt-0.5 text-sm leading-snug text-gray-600">{entry.body}</p>
            {entry.image && <EntryImage imageId={entry.image} />}
            {canShow && (
                <button
                    type="button"
                    onClick={() => showMe(entry)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                    Show me
                    <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </button>
            )}
        </li>
    );
}

/**
 * The notices whose window is still open, re-checked at the earliest end among them — the same
 * one-timer rule as the bar's, so a panel left open does not go on showing a notice that is over.
 */
function useCurrentNotices(notices: readonly Notice[]): readonly Notice[] {
    const [tick, setTick] = useState(0);

    const current = useMemo(() => {
        const now = Date.now();
        return notices.filter(n => {
            const until = Date.parse(n.validUntil);
            return Number.isNaN(until) || until > now;
        });
        // `tick` is the clock; see NoticeBar.
    }, [notices, tick]);

    useEffect(() => {
        const ends = current.map(n => Date.parse(n.validUntil)).filter(t => !Number.isNaN(t) && t > Date.now());
        if (ends.length === 0) return;
        const delay = Math.min(Math.min(...ends) - Date.now() + 1000, 2_000_000_000);
        const timer = window.setTimeout(() => setTick(t => t + 1), delay);
        return () => window.clearTimeout(timer);
    }, [current]);

    return current;
}

/** "Until Sat 26 Sep, 09:00" in the reader's own time zone, never the UTC of the wire. */
function untilLabel(validUntil: string): string {
    const d = new Date(validUntil);
    if (Number.isNaN(d.getTime())) return '';
    return `Until ${d.toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })}`;
}

function NoticeCard({ notice }: { notice: Notice }) {
    const style = NOTICE_STYLES[notice.severity] ?? NOTICE_STYLES.info;
    const Icon = notice.severity === 'info' ? Info : AlertTriangle;
    return (
        <li data-panel-notice-id={notice.id} className={`flex items-start gap-2.5 rounded-lg border p-3 ${style.bar}`}>
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.icon}`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold leading-tight">{notice.title}</h3>
                <p className="mt-0.5 text-sm leading-snug">{notice.body}</p>
                <p className="mt-1 text-[11px] opacity-75">{untilLabel(notice.validUntil)}</p>
            </div>
        </li>
    );
}

export function WhatsNewPanel() {
    const { panelOpen, closePanel, panelNewEntries, entries, notices } = useWhatsNew();
    const currentNotices = useCurrentNotices(notices);

    useEffect(() => {
        if (!panelOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closePanel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [panelOpen, closePanel]);

    if (!panelOpen) return null;

    const newIds = new Set(panelNewEntries.map(e => e.id));

    return (
        <>
            {/* Dimmed, but not modal: the panel is a reference, and clicking the app behind it
                is a legitimate way to close it. The dialog is the one that blocks. */}
            <div className="fixed inset-0 z-40 bg-black/20" onClick={closePanel} aria-hidden="true" />
            <aside
                role="dialog"
                aria-label="What's New"
                className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl"
            >
                <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <h2 className="text-sm font-bold text-gray-900">What&apos;s New</h2>
                    <button
                        type="button"
                        onClick={closePanel}
                        aria-label="Close"
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-4 py-3">
                    {currentNotices.length > 0 && (
                        <section className="mb-5" aria-label="Current notices">
                            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                                Current notices ({currentNotices.length})
                            </h3>
                            <ul className="flex flex-col gap-2">
                                {currentNotices.map(notice => (
                                    <NoticeCard key={notice.id} notice={notice} />
                                ))}
                            </ul>
                        </section>
                    )}

                    {panelNewEntries.length > 0 && (
                        <section className="mb-5">
                            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                                New for you
                            </h3>
                            <ul className="flex flex-col gap-2">
                                {panelNewEntries.map(entry => (
                                    <EntryCard key={entry.id} entry={entry} highlight />
                                ))}
                            </ul>
                        </section>
                    )}

                    <section>
                        <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                            Everything so far
                        </h3>
                        {entries.length === 0 ? (
                            // Honest rather than clever: an empty history is what a freshly
                            // adopted module looks like, and it says so.
                            <p className="text-sm text-gray-400">Nothing has been recorded yet.</p>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {entries.map(entry => (
                                    <EntryCard key={entry.id} entry={entry} highlight={false} />
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                <footer className="border-t border-gray-200 px-4 py-2">
                    <p className="text-[11px] text-gray-400">
                        {newIds.size > 0
                            ? `${newIds.size} new since you last looked`
                            : 'You are up to date'}
                    </p>
                </footer>
            </aside>
        </>
    );
}
